/**
 * Admin — Event Hosts management (super_admin only). Web mirror of
 * /app/mobile/app/admin/event-hosts.tsx.
 *
 * This screen intentionally never renders or transmits any Stripe key
 * material — the backend replaces every credential with a
 * `<field>_status: "ok" | "warning" | "missing"` token before sending.
 * Editing of the keys themselves is a Mongo-only action.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, RefreshCcw, Check, AlertTriangle, X, Plus } from "lucide-react";

/** Status pill for a single credential field. Renders NO key material —
 *  attackers with a screenshot / shoulder access cannot recover any part
 *  of a Stripe secret from this UI. Mirrors mobile StatusPill 1:1. */
function StatusPill({ label, status, testId }) {
  const s = status || "missing";
  const map = {
    ok:      { icon: Check,          cls: "bg-emerald-50 text-emerald-800 border-emerald-300", tip: "Configured & healthy" },
    warning: { icon: AlertTriangle,  cls: "bg-amber-50 text-amber-800 border-amber-300",       tip: "Configured but recently failed a check" },
    missing: { icon: X,              cls: "bg-red-50 text-red-800 border-red-300",             tip: "Not yet configured" },
  };
  const { icon: Icon, cls, tip } = map[s] || map.missing;
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${cls}`}
      title={tip}
      data-testid={testId}
    >
      <Icon size={12} />
      <span>{label}</span>
    </div>
  );
}

export default function AdminEventHostsPage() {
  const api = useApi();
  const nav = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/admin/event-hosts");
      if (r.ok) { const d = await r.json(); setRows(d.event_hosts || []); }
    } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Enter a display name for the new Event Host.");
    setCreating(true);
    try {
      const r = await api("/admin/event-hosts", { method: "POST", body: JSON.stringify({ name }) });
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Could not create Event Host");
      toast.success(`Event Host created — Host ID: ${d.host_id}. Paste the Stripe keys directly into the event_hosts Mongo document.`);
      setNewName(""); setShowAdd(false);
      load();
    } finally { setCreating(false); }
  };

  if (!isSuperAdmin) {
    return (
      <div>
        <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4"><ChevronLeft size={16}/> Back</button>
        <PageHeader title="Event Hosts" testId="event-hosts-header" />
        <Card className="p-6"><div className="font-semibold">Super admin access required.</div></Card>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => nav("/admin")} className="text-sm text-primary inline-flex items-center gap-1 mb-4"><ChevronLeft size={16}/> Back</button>
      <PageHeader
        title="Stripe accounts / Event Hosts"
        subtitle="Each Event Host maps a Stripe account name to its Live and Test credentials."
        testId="event-hosts-header"
        actions={
          <Button variant="outline" onClick={load} disabled={loading} data-testid="hosts-refresh">
            <RefreshCcw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
        }
      />

      <Card className="p-4 mb-4 bg-muted/40 border-border" data-testid="hosts-guidance">
        <div className="text-sm text-muted-foreground">
          Fill the Stripe keys directly in the <code className="px-1 py-0.5 rounded bg-white border border-border text-xs">event_hosts</code> Mongo collection.
          For safety this screen never renders any part of the keys themselves — only a status per field:{" "}
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><Check size={12}/> configured &amp; healthy</span>,{" "}
          <span className="inline-flex items-center gap-1 text-amber-800 font-medium"><AlertTriangle size={12}/> configured but recently failed a check</span>,{" "}
          <span className="inline-flex items-center gap-1 text-destructive font-medium"><X size={12}/> not yet configured</span>.
        </div>
      </Card>

      {loading && rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading Event Hosts…</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const liveStatuses = [
              r.live_secret_key_status,
              r.live_publishable_key_status,
              r.live_webhook_secret_status,
              r.live_account_id_status,
            ];
            const anyWarning = liveStatuses.some((s) => s === "warning");
            const allOk = liveStatuses.every((s) => s === "ok");
            const liveBadge = anyWarning
              ? { text: "Live keys — check failing", variant: "destructive" }
              : allOk
                ? { text: "Live keys set", variant: "secondary" }
                : { text: "Live keys missing", variant: "destructive" };
            return (
              <Card key={r.host_id} className="p-5" data-testid={`host-card-${r.host_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="text-lg font-semibold min-w-0 truncate">{r.name}</h3>
                  <Badge variant={liveBadge.variant} data-testid={`host-live-badge-${r.host_id}`}>{liveBadge.text}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Host ID · <code className="px-1 py-0.5 rounded bg-muted border border-border">{r.host_id}</code>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <StatusPill label="Live secret"          status={r.live_secret_key_status}         testId={`ss-live-secret-${r.host_id}`} />
                  <StatusPill label="Live publishable"     status={r.live_publishable_key_status}    testId={`ss-live-pub-${r.host_id}`} />
                  <StatusPill label="Live webhook secret"  status={r.live_webhook_secret_status}     testId={`ss-live-webhook-${r.host_id}`} />
                  <StatusPill label="Live account id"      status={r.live_account_id_status}         testId={`ss-live-acct-${r.host_id}`} />
                  <StatusPill label="Test secret"          status={r.test_secret_key_status}         testId={`ss-test-secret-${r.host_id}`} />
                  <StatusPill label="Test publishable"     status={r.test_publishable_key_status}    testId={`ss-test-pub-${r.host_id}`} />
                  <StatusPill label="Test webhook secret"  status={r.test_webhook_secret_status}     testId={`ss-test-webhook-${r.host_id}`} />
                  <StatusPill label="Test account id"      status={r.test_account_id_status}         testId={`ss-test-acct-${r.host_id}`} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-6">
        {showAdd ? (
          <Card className="p-5" data-testid="hosts-add-form">
            <h3 className="text-lg font-semibold">Add Stripe Account / Event Host</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Creates a placeholder row with just the display name. Paste the Stripe keys into the new Mongo document afterwards.
            </p>
            <div className="space-y-1">
              <Label>Display name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Middlesex Chapter Almoner's Fund"
                data-testid="host-new-name"
                autoFocus
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={create} disabled={creating} data-testid="host-create">
                {creating ? "Creating…" : "Create"}
              </Button>
              <Button variant="ghost" onClick={() => { setShowAdd(false); setNewName(""); }} data-testid="host-cancel">
                Cancel
              </Button>
            </div>
          </Card>
        ) : (
          <Button onClick={() => setShowAdd(true)} data-testid="host-add">
            <Plus size={14} className="mr-2" />Add Stripe Account / Event Host
          </Button>
        )}
      </div>
    </div>
  );
}
